import { useState } from 'react';
import { Box, Button, Chip, Drawer, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import { HotelSearchFilterFields } from './HotelSearchFilterFields';

export function HotelResultFilters({
  value,
  onChange,
  disabled,
  count,
  total,
  summary,
}: {
  value: HotelSearchFilters;
  onChange: (filters: HotelSearchFilters) => void;
  disabled: boolean;
  count: number;
  total?: number | null;
  summary?: string;
}) {
  const { t } = useTranslation();
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const [draft, setDraft] = useState<HotelSearchFilters | null>(null);
  const activeCount = value.board_categories.length + (value.refundability ? 1 : 0);
  const clear = (filters: HotelSearchFilters): HotelSearchFilters => ({
    ...filters,
    board_categories: [],
    refundability: null,
  });
  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2">
          {summary ?? (total != null ? t('hotelSearch.hotelCount', { count, total }) : t('hotelSearch.resultFilters'))}
        </Typography>
        {mobile && (
          <Button variant="outlined" disabled={disabled} onClick={() => setDraft(value)}>
            {t('hotelSearch.filtersButton')}
            {activeCount > 0 ? ` · ${activeCount}` : ''}
          </Button>
        )}
      </Stack>
      {!mobile && (
        <Box>
          <HotelSearchFilterFields value={value} onChange={onChange} disabled={disabled} />
          {activeCount > 0 && (
            <Button disabled={disabled} onClick={() => onChange(clear(value))}>
              {t('hotelSearch.clearFilters')}
            </Button>
          )}
        </Box>
      )}
      {mobile && activeCount > 0 && (
        <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
          {value.board_categories.map((board) => (
            <Chip
              key={board}
              size="small"
              label={t(`hotelSearch.boards.${board}`)}
              disabled={disabled}
              onDelete={() =>
                onChange({ ...value, board_categories: value.board_categories.filter((item) => item !== board) })
              }
            />
          ))}
          {value.refundability && (
            <Chip
              size="small"
              label={t(`hotelSearch.refundability.${value.refundability}`)}
              disabled={disabled}
              onDelete={() => onChange({ ...value, refundability: null })}
            />
          )}
        </Stack>
      )}
      <Drawer
        anchor="bottom"
        open={mobile && draft !== null}
        onClose={() => setDraft(null)}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          'aria-labelledby': 'hotel-result-filters-title',
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '90dvh' },
        }}
      >
        {draft && (
          <Stack gap={3} sx={{ p: 2, pb: 'max(16px, env(safe-area-inset-bottom))', overflowY: 'auto' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography id="hotel-result-filters-title" variant="h6">
                {t('hotelSearch.filtersButton')}
              </Typography>
              <Button onClick={() => setDraft(null)}>{t('hotelSearch.closeFilters')}</Button>
            </Stack>
            <HotelSearchFilterFields value={draft} onChange={setDraft} disabled={disabled} />
            <Button disabled={disabled} onClick={() => setDraft(clear(draft))}>
              {t('hotelSearch.clearFilters')}
            </Button>
            <Button
              variant="contained"
              disabled={disabled}
              onClick={() => {
                onChange(draft);
                setDraft(null);
              }}
            >
              {t('hotelSearch.applyFilters')}
            </Button>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
